import React from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { Headphones } from 'react-feather';
import {
  style,
  Track,
  Comment as CommentType,
  useCurrentUser,
  useTrackFeedback,
  useIsReleaseTrack,
} from '../../api';
import RelatedTracks from './RelatedTracks';
import Comment from './Comments/Comment';
import CommentForm from './Comments/CommentForm';
import { BigBorderBtn } from '../common/Buttons';
import { CenterLoader } from '../common/Miscellanea';
import { tablet, desktop, noDesktop, useIsMobile } from '../../style/constants';
import { TextRegular12 } from '../common/Text';
import ReleasedTrackAdditionalInfo from './ReleasedTrackAdditionalInfo';
import { Button } from '../../VuexyDesign/atoms/_index';
import { TextBold14, TextRegular14 } from '../../VuexyDesign/atoms/Text';

const TrackAdditionalInfo = ({
  className,
  track,
  sendTrackComment,
  comments,
  userIsLoggedIn,
  deleteComment,
  userId,
  showLoadMoreBtn,
  loadMore,
  fetching,
  totalComments,
}: {
  className?: string;
  track: Track;
  sendTrackComment: (comment: string) => void;
  comments: (CommentType & { replies: CommentType[] })[];
  deleteComment: (
    commentId: number,
    parent: {
      parentCommentId?: number;
      trackId?: number;
    },
  ) => void;
  userIsLoggedIn: boolean;
  userId?: number;
  showLoadMoreBtn: boolean;
  loadMore: () => void;
  fetching: boolean;
  totalComments: number;
}) => {
  const { t } = useTranslation('tracks');
  const { id, requested_my_feedback } = track;
  const { data: feedback = [] } = useTrackFeedback(track.id);
  const user = useCurrentUser();
  const isMobile = useIsMobile();
  const isTrackReleased = useIsReleaseTrack(track?.release_status);
  const commentsSorted = [
    ...feedback,
    ...comments.filter((comment) => !comment.feedback),
  ];

  if (userIsLoggedIn && (!user || !user.id || !track)) {
    return <CenterLoader />;
  }

  if (isTrackReleased) {
    return (
      <Container>
        <ReleasedTrackAdditionalInfo
          track={track}
          comments={comments}
          loadMore={loadMore}
          showLoadMoreBtn={showLoadMoreBtn}
          fetching={fetching}
        />
      </Container>
    );
  }

  console.log(commentsSorted);

  return (
    <div
      className={className}
      style={isMobile ? { marginLeft: 15, marginRight: 15 } : {}}
    >
      <StyledContainer>
        <Row>
          <Col lg={9} noGutter={isMobile}>
            <FlexBox>
              {userIsLoggedIn && (
                <>
                  {requested_my_feedback && (
                    <FeedbackRequestedBox className="d-flex justify-content-between align-items-center br-5">
                      <Col lg={8}>
                        <TextBold14>
                          <Trans
                            i18nKey="tracks:feedbackRequest"
                            values={{ username: track.user.display_name }}
                          />
                        </TextBold14>
                        <TextRegular14>
                          {t('tracks:feedbackEarnings') as string}
                        </TextRegular14>
                      </Col>
                      <Col lg={3} className="button-col">
                        <Button color="info">
                          <div style={{ whiteSpace: 'nowrap' }}>
                            <Headphones
                              size={14}
                              style={{ marginRight: '10px' }}
                            />
                            {t('tracks:giveFeedback') as string}
                          </div>
                        </Button>
                      </Col>
                    </FeedbackRequestedBox>
                  )}
                  {!requested_my_feedback && user && user.id && (
                    <TrackDescription>
                      <CommentForm onSend={sendTrackComment} />
                    </TrackDescription>
                  )}
                </>
              )}
              {comments && (
                <TrackDescription style={{ paddingTop: isMobile ? 18 : 10 }}>
                  {totalComments > 0 && (
                    <TextRegular12>
                      {totalComments} {t('tracks:comments') as string}
                    </TextRegular12>
                  )}
                  {commentsSorted.map((comment) => (
                    <Comment
                      {...comment}
                      userId={userId}
                      deleteComment={deleteComment}
                      userIsLoggedIn={userIsLoggedIn}
                      trackId={id}
                      key={comment.id}
                    />
                  ))}
                  {showLoadMoreBtn && (
                    <BigBorderBtn onClick={loadMore}>
                      {t('tracks:loadMore') as string}
                    </BigBorderBtn>
                  )}
                  {fetching && <CenterLoader />}
                </TrackDescription>
              )}
            </FlexBox>
          </Col>
          {track.genre && (
            <Col lg={3} noGutter={isMobile}>
              <RelatedTracks genreId={track.genre.id} />
            </Col>
          )}
        </Row>
      </StyledContainer>
    </div>
  );
};

const StyledContainer = styled(Container)`
  margin: 0px 15px;
`;

const FeedbackRequestedBox = styled(Row)`
  margin: 0px 0px 15px 0px;
  padding: 12px;

  border: 1px solid ${style.borderGrey};

  span {
    display: block;
  }

  @media ${tablet} {
    span {
      margin-right: auto;
      display: block;
    }
  }

  ${Col} {
    padding: 0px;
  }

  @media ${noDesktop} {
    text-align: center;
    .button-col {
      display: flex;
      justify-content: center;
      margin-top: 10px;
      padding: 0px;
      & > button {
        width: 100%;
      }
    }
  }

  @media ${desktop} {
    .button-col {
      display: flex;
      justify-content: flex-end;
      padding: 0px;
    }
  }
`;

const FlexBox = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  color: ${style.black};
`;

const TrackDescription = styled.div`
  display: flex;
  flex-direction: column;
`;

export default styled(TrackAdditionalInfo)`
  padding-top: 30px;
`;
